import { useState, useEffect, useContext, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Send28Filled } from "@fluentui/react-icons";

import styles from "./EmailComposer.module.css";
import { LoginContext } from "../../loginContext";
import { requireLogin } from "../../authConfig";

interface Props {
    onSend: (subject: string, body: string) => void;
    disabled: boolean;
    vaeEnabled?: boolean;
    initSubject?: string;
    initBody?: string;
    placeholderSubject?: string;
    placeholderBody?: string;
    hasAnswers?: boolean; // New prop to indicate if there are answers
    onClearAll?: () => void; // New prop to clear both email fields and conversation results
}

export const EmailComposer = ({
    onSend,
    disabled,
    vaeEnabled = false,
    initSubject,
    initBody,
    placeholderSubject,
    placeholderBody,
    hasAnswers = false,
    onClearAll
}: Props) => {
    const [subject, setSubject] = useState<string>("");
    const [body, setBody] = useState<string>("");
    const { loggedIn } = useContext(LoginContext);
    const { t } = useTranslation();
    const [isComposing, setIsComposing] = useState(false);
    const bodyRef = useRef<HTMLTextAreaElement>(null);

    const autoResize = useCallback(() => {
        const el = bodyRef.current;
        if (el) {
            el.style.height = "auto";
            el.style.height = el.scrollHeight + "px";
        }
    }, []);

    useEffect(() => {
        autoResize();
    }, [body, autoResize]);

    const currentTime = new Date().toLocaleString(t("locale") || "en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true
    });

    useEffect(() => {
        if (initSubject) {
            setSubject(initSubject);
        }
    }, [initSubject]);

    useEffect(() => {
        if (initBody) {
            setBody(initBody);
        }
    }, [initBody]);

    const sendQuestion = () => {
        if (disabled || !body.trim()) {
            return;
        }
        // In VAE mode, send empty subject, only body content
        const subjectToSend = vaeEnabled ? "" : subject;
        onSend(subjectToSend, body);
        // Don't clear the content - leave it greyed out for editing
    };

    const onEnterPress = (ev: React.KeyboardEvent<Element>) => {
        if (isComposing) return;
        // Ctrl/Cmd + Enter sends the message
        if (ev.key === "Enter" && (ev.ctrlKey || ev.metaKey)) {
            ev.preventDefault();
            sendQuestion();
        }
    };

    const handleCompositionStart = () => {
        setIsComposing(true);
    };

    const handleCompositionEnd = () => {
        setIsComposing(false);
    };

    const onSubjectChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
        setSubject(ev.target.value || "");
    };

    const onBodyChange = (ev: React.ChangeEvent<HTMLTextAreaElement>) => {
        setBody(ev.target.value || "");
    };
    const disableRequiredAccessControl = requireLogin && !loggedIn;
    const sendQuestionDisabled = disabled || !body.trim() || disableRequiredAccessControl;

    // Show New Email button if there's content in the fields OR if there are answers
    const hasContent = subject.trim() !== "" || body.trim() !== "";
    const showNewEmailButton = hasAnswers || hasContent;

    const subjectPlaceholder = disableRequiredAccessControl
        ? t("email.loginPlaceholder")
        : placeholderSubject || t("email.emailSubjectPlaceholder") || "Enter email subject...";

    const bodyPlaceholder = disableRequiredAccessControl
        ? t("email.loginPlaceholder")
        : placeholderBody || t("email.emailBodyPlaceholder") || "Compose your email message...";

    return (
        <div className={`${styles.container} ${hasAnswers ? styles.hasAnswers : ""}`}>
            <div className={styles.emailHeader}>
                <div className={styles.headerLeft}>
                    <span className={styles.emailIcon}>✉️</span>
                    <div className={styles.emailMeta}>
                        <div className={styles.emailTitle}>{vaeEnabled ? t("emailVAE.banner") : t("email.banner")}</div>
                        <div className={styles.emailTimestamp}>{currentTime}</div>
                    </div>
                </div>{" "}
                {showNewEmailButton && (
                    <button
                        className={styles.clearButton}
                        onClick={() => {
                            setSubject("");
                            setBody("");
                            // Call onClearAll to clear conversation results as well
                            if (onClearAll) {
                                onClearAll();
                            }
                        }}
                        title="Clear and compose new email"
                    >
                        {t("email.newEmail")}
                    </button>
                )}
            </div>

            {!vaeEnabled && (
                <div className={styles.subjectSection}>
                    <label className={styles.fieldLabel}>{t("email.subject")}</label>
                    <input
                        type="text"
                        className={`${styles.subjectInput} ${hasAnswers ? styles.greyedOut : ""}`}
                        disabled={disableRequiredAccessControl}
                        placeholder={subjectPlaceholder}
                        value={subject}
                        onChange={onSubjectChange}
                    />
                </div>
            )}

            <div className={styles.bodySection}>
                <label className={styles.fieldLabel}>{vaeEnabled ? t("emailVAE.body") : t("email.body")}</label>
                <textarea
                    ref={bodyRef}
                    className={`${styles.bodyInput} ${hasAnswers ? styles.greyedOut : ""}`}
                    disabled={disableRequiredAccessControl}
                    placeholder={bodyPlaceholder}
                    value={body}
                    onChange={onBodyChange}
                    onKeyDown={onEnterPress}
                    onCompositionStart={handleCompositionStart}
                    onCompositionEnd={handleCompositionEnd}
                />
            </div>

            <div className={styles.footerActions}>
                <div className={styles.characterCount}>
                    {body.length} {t("email.characters")}
                </div>
                <div className={styles.sendButtonWrapper}>
                    <span style={{ fontSize: "0.875rem", color: "#6b7280" }}>{sendQuestionDisabled ? "" : t("email.sendHelp")}</span>
                    <button className={styles.sendButton} disabled={sendQuestionDisabled} onClick={sendQuestion} title="Send for compliance review">
                        <Send28Filled className={styles.sendIcon} />
                        {t("email.enter")}
                    </button>
                </div>
            </div>
        </div>
    );
};
